import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  History, Clock, User, Archive, RotateCcw, Trash2, 
  ChevronRight, ChevronDown, Filter, Calendar, Database,
  AlertTriangle, CheckCircle2, XCircle, Loader2, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category configuration for display
const CATEGORY_CONFIG = {
  census: { icon: Database, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  clinical: { icon: History, color: 'text-rose-600', bgColor: 'bg-rose-50' },
  financial: { icon: Database, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
  compliance: { icon: CheckCircle2, color: 'text-violet-600', bgColor: 'bg-violet-50' },
  workforce: { icon: User, color: 'text-amber-600', bgColor: 'bg-amber-50' },
  program: { icon: Database, color: 'text-cyan-600', bgColor: 'bg-cyan-50' },
  pips: { icon: History, color: 'text-pink-600', bgColor: 'bg-pink-50' },
  insights: { icon: History, color: 'text-[#00b0ab]', bgColor: 'bg-[#00b0ab]/10' }
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

// Format field name for display
const formatFieldName = (field) => {
  return field
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Format value for display
const formatValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'number') {
    if (value < 1 && value > 0) return `${(value * 100).toFixed(1)}%`;
    return value.toLocaleString();
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

/**
 * DataHistoryPanel Component
 * 
 * A reusable component that shows data history for any category.
 * Includes timeline view, audit log, and archive/restore functionality.
 * 
 * Props:
 * - category: string - The data category to display (census, clinical, financial, etc.)
 * - title: string - Optional title override
 * - defaultExpanded: boolean - Whether to start expanded
 * - showAuditLog: boolean - Whether to show audit log tab
 * - isAdmin: boolean - Whether user has admin privileges (for archive/restore)
 */
export function DataHistoryPanel({ 
  category, 
  title, 
  defaultExpanded = false,
  showAuditLog = true,
  isAdmin = false
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [activeTab, setActiveTab] = useState('timeline');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState({ records: [], total: 0 });
  const [auditLog, setAuditLog] = useState({ entries: [], total: 0 });
  const [archived, setArchived] = useState({ archived_records: [], total: 0 });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [processing, setProcessing] = useState(false);

  const token = localStorage.getItem('qapi-token');
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.census;
  const Icon = config.icon;

  const fetchHistory = useCallback(async () => {
    if (!expanded || !token) return;
    
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/data-history/${category}?limit=50&include_archived=${includeArchived}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  }, [category, token, expanded, includeArchived]);

  const fetchAuditLog = useCallback(async () => {
    if (!showAuditLog || !token) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/data-history/audit/log?category=${category}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setAuditLog(data);
      }
    } catch (error) {
      console.error('Error fetching audit log:', error);
    }
  }, [category, token, showAuditLog]);

  const fetchArchived = useCallback(async () => {
    if (!isAdmin || !token) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/data-history/archived/all?category=${category}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setArchived(data);
      }
    } catch (error) {
      console.error('Error fetching archived:', error);
    }
  }, [category, token, isAdmin]);

  useEffect(() => {
    if (expanded) {
      fetchHistory();
      if (showAuditLog) fetchAuditLog();
      if (isAdmin) fetchArchived();
    }
  }, [expanded, fetchHistory, fetchAuditLog, fetchArchived, showAuditLog, isAdmin]);

  const handleArchive = async () => {
    if (!selectedRecord) return;
    
    setProcessing(true);
    try {
      const identifier = selectedRecord.data?.period_start || 
                        selectedRecord.data?.created_at ||
                        selectedRecord.data?.period_date;
      
      const res = await fetch(`${API_URL}/api/data-history/archive`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          category,
          record_identifier: identifier
        })
      });
      
      if (res.ok) {
        toast.success('Record archived successfully');
        setShowArchiveDialog(false);
        setSelectedRecord(null);
        fetchHistory();
        fetchArchived();
        fetchAuditLog();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to archive record');
      }
    } catch (error) {
      toast.error('Error archiving record');
    } finally {
      setProcessing(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedRecord) return;
    
    setProcessing(true);
    try {
      const identifier = selectedRecord.data?.period_start || 
                        selectedRecord.data?.created_at ||
                        selectedRecord.data?.period_date;
      
      const res = await fetch(`${API_URL}/api/data-history/restore`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          category,
          record_identifier: identifier
        })
      });
      
      if (res.ok) {
        toast.success('Record restored successfully');
        setShowRestoreDialog(false);
        setSelectedRecord(null);
        fetchHistory();
        fetchArchived();
        fetchAuditLog();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to restore record');
      }
    } catch (error) {
      toast.error('Error restoring record');
    } finally {
      setProcessing(false);
    }
  };

  const displayTitle = title || history.display_name || formatFieldName(category);

  return (
    <Card className="border-0 shadow-sm" data-testid={`data-history-${category}`}>
      <CardHeader 
        className={`cursor-pointer transition-colors hover:bg-muted/50 ${config.bgColor} rounded-t-lg`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-white/80 ${config.color}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4 text-muted-foreground" />
                {displayTitle} History
              </CardTitle>
              <CardDescription className="text-xs">
                {history.total} records • Click to {expanded ? 'collapse' : 'expand'}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {history.total} records
            </Badge>
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="pt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="timeline" className="text-xs">
                  <Clock className="h-3 w-3 mr-1" />
                  Timeline
                </TabsTrigger>
                {showAuditLog && (
                  <TabsTrigger value="audit" className="text-xs">
                    <User className="h-3 w-3 mr-1" />
                    Audit Log
                  </TabsTrigger>
                )}
                {isAdmin && (
                  <TabsTrigger value="archived" className="text-xs">
                    <Archive className="h-3 w-3 mr-1" />
                    Archived ({archived.total})
                  </TabsTrigger>
                )}
              </TabsList>
              
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-xs text-muted-foreground">
                  <input 
                    type="checkbox" 
                    checked={includeArchived}
                    onChange={(e) => setIncludeArchived(e.target.checked)}
                    className="h-3 w-3"
                  />
                  Show archived
                </label>
              </div>
            </div>
            
            <TabsContent value="timeline" className="mt-0">
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : history.records.length === 0 ? (
                <Alert>
                  <AlertDescription>No history records found for this category.</AlertDescription>
                </Alert>
              ) : (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {history.records.map((record, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border ${record.is_archived ? 'bg-muted/50 opacity-60' : 'bg-white hover:bg-muted/30'} transition-colors`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Calendar className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs font-medium">
                                {formatDate(record.timestamp)}
                              </span>
                              {record.is_archived && (
                                <Badge variant="secondary" className="text-[10px]">
                                  Archived
                                </Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                              {Object.entries(record.key_values || {}).slice(0, 4).map(([key, value]) => (
                                <div key={key} className="text-muted-foreground">
                                  <span className="font-medium">{formatFieldName(key)}:</span>{' '}
                                  <span className="text-foreground">{formatValue(value)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          {isAdmin && !record.is_archived && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs text-muted-foreground hover:text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRecord(record);
                                setShowArchiveDialog(true);
                              }}
                            >
                              <Archive className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>
            
            {showAuditLog && (
              <TabsContent value="audit" className="mt-0">
                {auditLog.entries.length === 0 ? (
                  <Alert>
                    <AlertDescription>No audit log entries for this category.</AlertDescription>
                  </Alert>
                ) : (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {auditLog.entries.map((entry, idx) => (
                        <div key={idx} className="p-3 rounded-lg border bg-white">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge 
                              variant={entry.action === 'archive' ? 'destructive' : 
                                      entry.action === 'restore' ? 'default' : 'secondary'}
                              className="text-[10px]"
                            >
                              {entry.action}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(entry.timestamp)}
                            </span>
                          </div>
                          <div className="text-xs">
                            <span className="font-medium">{entry.user_name || entry.user_email}</span>
                            <span className="text-muted-foreground"> performed </span>
                            <span className="font-medium">{entry.action}</span>
                            <span className="text-muted-foreground"> on record </span>
                            <span className="font-mono text-[10px] bg-muted px-1 rounded">
                              {entry.record_identifier}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </TabsContent>
            )}
            
            {isAdmin && (
              <TabsContent value="archived" className="mt-0">
                {archived.archived_records.length === 0 ? (
                  <Alert>
                    <AlertDescription>No archived records.</AlertDescription>
                  </Alert>
                ) : (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {archived.archived_records.map((record, idx) => (
                        <div key={idx} className="p-3 rounded-lg border bg-muted/30">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Archive className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">
                                  Archived {formatDate(record.archived_at)}
                                </span>
                                <span className="text-xs">
                                  by <span className="font-medium">{record.archived_by_name || record.archived_by}</span>
                                </span>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                {Object.entries(record.data || {}).slice(0, 4).map(([key, value]) => (
                                  <div key={key} className="text-muted-foreground">
                                    <span className="font-medium">{formatFieldName(key)}:</span>{' '}
                                    <span>{formatValue(value)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => {
                                setSelectedRecord(record);
                                setShowRestoreDialog(true);
                              }}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Restore
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      )}
      
      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Archive Record
            </DialogTitle>
            <DialogDescription>
              This will archive the record. It will be hidden from the main view but can be restored later.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to archive this record?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleArchive}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Archive className="h-4 w-4 mr-2" />
              )}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-emerald-500" />
              Restore Record
            </DialogTitle>
            <DialogDescription>
              This will restore the archived record and make it visible again.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to restore this record?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRestore}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/**
 * DataHistorySummary Component
 * 
 * Shows a summary card with all data categories and their record counts.
 * Useful for admin dashboards and system overview pages.
 */
export function DataHistorySummary({ onCategorySelect }) {
  const [summary, setSummary] = useState({ categories: {} });
  const [loading, setLoading] = useState(true);
  
  const token = localStorage.getItem('qapi-token');

  useEffect(() => {
    const fetchSummary = async () => {
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/data-history/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSummary(data);
        }
      } catch (error) {
        console.error('Error fetching summary:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [token]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="data-history-summary">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Data History Overview
        </CardTitle>
        <CardDescription>
          View historical data and audit logs across all categories
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(summary.categories || {}).map(([key, cat]) => {
            const config = CATEGORY_CONFIG[key] || CATEGORY_CONFIG.census;
            const Icon = config.icon;
            
            return (
              <div 
                key={key}
                className={`p-4 rounded-lg ${config.bgColor} cursor-pointer hover:opacity-80 transition-opacity`}
                onClick={() => onCategorySelect?.(key)}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`h-4 w-4 ${config.color}`} />
                  <span className="font-medium text-sm">{cat.display_name}</span>
                </div>
                <div className="text-2xl font-bold">{cat.total_records}</div>
                <div className="text-xs text-muted-foreground">
                  {cat.active_records} active • {cat.archived_records} archived
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default DataHistoryPanel;
